import { Component } from '@theme/component';
import { debounce, onDocumentLoaded } from '@theme/utilities';
import { morph } from '@theme/morph';
import { DialogCloseEvent } from '@theme/dialog';

class PredictiveSearchComponent extends Component {
  requiredRefs = ['searchInput', 'predictiveSearchResults', 'resetButton'];

  #controller = new AbortController();
  #abortController = null;

  connectedCallback() {
    super.connectedCallback();
    const { dialog } = this;
    const { signal } = this.#controller;

    if (this.refs.searchInput?.value?.length) {
      this.#updateResetButtonVisibility();
    }

    if (dialog) {
      document.addEventListener(
        'keydown',
        this.#handleKeyboardShortcut.bind(this),
        { signal }
      );

      dialog.addEventListener(
        DialogCloseEvent.eventName,
        this.#handleDialogClose.bind(this),
        { signal }
      );

      this.addEventListener(
        'click',
        this.#handleModalClick.bind(this),
        { signal }
      );
    }

    this.refs.searchInput.addEventListener(
      'input',
      debounce(() => this.#onInput(), 300)
    );

    onDocumentLoaded(() => this.#resetSearch(false));
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller.abort();
  }

  #onInput() {
    const searchTerm = this.refs.searchInput.value.trim();
    this.#updateResetButtonVisibility();

    if (!searchTerm) {
      this.#resetSearch();
      return;
    }

    this.#getSearchResults(searchTerm);
  }

  #updateResetButtonVisibility() {
    if (!this.refs.resetButton) return;
    this.refs.resetButton.style.display =
      this.refs.searchInput.value.length > 0 ? 'block' : 'none';
  }

  #createAbortController() {
    if (this.#abortController) this.#abortController.abort();
    this.#abortController = new AbortController();
    return this.#abortController;
  }

  #handleModalClick = (event) => {
    const isInteractive = event.target.closest('button, a, input');
    if (!isInteractive) this.refs.searchInput?.focus();
  };

  #handleKeyboardShortcut = (event) => {
    if (event.metaKey && event.key === 'k') {
      this.dialog?.toggleDialog();
    }
  };

  #handleDialogClose = () => {
    this.#resetSearch();
  };

  #resetSearch(keepFocus = true) {
    if (this.refs.predictiveSearchResults) {
      this.refs.predictiveSearchResults.innerHTML = '';
    }
    if (!keepFocus && this.refs.searchInput) {
      this.refs.searchInput.value = '';
    }
    this.#updateResetButtonVisibility();
  }

  async #getSearchResults(searchTerm) {
    if (!this.dataset.sectionId) return;

    const abortController = this.#createAbortController();
    const resultsContainer = this.refs.predictiveSearchResults;

    try {
      const shopifyUrl = new URL(
        Theme.routes.predictive_search_url,
        location.origin
      );
      shopifyUrl.searchParams.set('q', searchTerm);
      shopifyUrl.searchParams.set('section_id', this.dataset.sectionId);
      shopifyUrl.searchParams.set('resources[options][fields]', 'title,vendor,product_type,variants.sku,body');

      const response = await fetch(shopifyUrl.toString(), {
        signal: abortController.signal,
      });

      if (!response.ok) throw new Error('Search request failed');

      const shopifyHTML = await response.text();

      if (abortController.signal.aborted) return;

      // Render the HTML returned from the predictive-search.liquid section
      morph(resultsContainer, shopifyHTML || '');

    } catch (e) {
      if (e.name !== 'AbortError') {
        console.error('Predictive search failed:', e);
      }
    }
  }
}

if (!customElements.get('predictive-search-component')) {
  customElements.define(
    'predictive-search-component',
    PredictiveSearchComponent
  );
}